export type CogShowcaseKind =
  | 'motion'
  | 'video'
  | 'voxel'
  | 'clean'
  | 'depth'
  | 'vector'
  | 'upscale'
  | 'stems'
  | 'cutout'
  | 'speech'
  | 'text'
  | 'image'
  | 'music';

export interface CogShowcase {
  slug: string;
  aliases?: string[];
  template: string;
  title: string;
  eyebrow: string;
  description: string;
  hardware: string;
  output: string;
  kind: CogShowcaseKind;
  input: Record<string, string | number | boolean>;
  source?: string;
  upstream?: string;
  tags: string[];
  inputImage?: string;
  inputVideo?: string;
  inputAudio?: string;
  outputMedia?: string;
  outputFile?: string;
  outputText?: string;
  outputNote: string;
}

const robot = '/static/animation-library/image3d/robot.webp';

export const cogShowcases: CogShowcase[] = [
  {
    slug: 'fastserve-qwen-27b', aliases: ['qwen3.8-27b', 'fastserve-qwen'], template: 'fastserve-qwen-27b',
    title: 'Qwen3.8-27B Uncensored GGUF', eyebrow: 'Prompt → reasoning, code, tool calls',
    description: 'Uncensored Qwen3.8-27B Q4_K_M on llama.cpp, tuned for a 32 GB RTX 5090 with flash attention, Q8 KV cache, prompt caching, and retained-head MTP speculative decoding. A stock Unsloth Dynamic 3.0 variant is selectable for A/B tests.',
    hardware: 'RTX 5090 32GB', output: 'Streaming text', kind: 'text',
    input: { prompt: 'Refactor this Go handler to stream results, then explain the tradeoff in two sentences.', system: 'You are a precise staff engineer.', max_tokens: 800, temperature: 0.7 },
    outputText: 'Stream the rows as you scan them: keep the *sql.Rows open, write each JSON object into the http.ResponseWriter, and Flush() every N records.\n\nThe tradeoff is that you cannot set a Content-Length or fail cleanly mid-response — once the first byte is out, an error becomes a truncated body rather than a 500.',
    source: 'https://github.com/lee101/fastserve-qwen-3.8-27b', upstream: 'https://huggingface.co/Qwen/Qwen3.8-27B',
    tags: ['Qwen3.8', 'GGUF', 'Uncensored', 'MTP', 'Dynamic 3.0'],
    outputNote: 'OpenAI-compatible /v1 plus Cog predictions from the same container',
  },
  {
    slug: 'anima-2.9b', template: 'anima-2.9b', title: 'Anima 2.9B', eyebrow: 'Prompt → anime illustration',
    description: 'Run the expanded 2.9B Anima diffusion transformer through a native BF16 Cog with TF32, SDPA, batched guidance, a precompiled torch.compile cache, and a RunPod worker that returns to zero after the burst.',
    hardware: 'L40S 48GB', output: 'WebP, PNG, or JPEG', kind: 'image',
    input: {
      prompt: 'masterpiece, best quality, 1girl, wind-swept cape, luminous city at dusk, detailed background, dramatic illustration',
      negative_prompt: 'low quality, blurry, malformed hands', width: 832, height: 1216,
      num_inference_steps: 28, guidance_scale: 4, seed: 42, output_format: 'webp',
    },
    source: 'https://github.com/lee101/anima-omniserve-native', upstream: 'https://huggingface.co/Gazingstars123/Anima-2.9B',
    tags: ['Anima', 'Cosmos', 'serverless', 'scale-to-zero'],
    outputNote: 'A generated image appears here after the run completes.',
  },
  {
    slug: 'ardy-motion', aliases: ['ardy'], template: 'ardy', title: 'ARDY Motion', eyebrow: 'Text → live pose stream',
    description: 'Direct a humanoid with natural-language motion, waypoints, and WASD controls. ARDY streams structured pose frames at 20 FPS for games, avatars, and animation tools.',
    hardware: 'RTX 3090', output: 'JSON pose stream', kind: 'motion',
    input: { prompt: 'Walk forward, turn left, then break into a joyful run.', steps: 40 },
    source: 'https://github.com/lee101/appnz-ardy', upstream: 'https://research.nvidia.com/labs/toronto-ai/ardy/',
    tags: ['real-time', 'websocket', 'humanoid motion'], outputNote: 'Live session output · 20 FPS skeleton frames',
  },
  {
    slug: 'liveavatar-appnz', aliases: ['liveavatar'], template: 'liveavatar', title: 'LiveAvatar', eyebrow: 'Portrait + audio → character video',
    description: 'Turn one character image and a speech or singing track into expressive long-form video with face, body, camera, and scene guidance.',
    hardware: 'H100 80GB', output: 'MP4 video', kind: 'video',
    input: { image: robot, audio: 'voice.wav', prompt: 'Friendly presenter, natural gestures, soft studio light.', quality: 'standard' },
    inputImage: robot, outputMedia: '/static/uploads/playground/happy-horse-v1.1/image-to-video.mp4',
    source: 'https://github.com/lee101/liveavatar-app-nz', tags: ['audio-driven', 'character', 'long-form'],
    outputNote: 'Video-format preview · durable LiveAvatar example pending',
  },
  {
    slug: 'voxel-3d', template: 'voxel-3d', title: 'Voxel 3D', eyebrow: 'Image → browser-ready 3D',
    description: 'Isolate an object, reconstruct its surface with TripoSR, optionally voxelize it, and return a portable GLB ready for the web or a game engine.',
    hardware: 'RTX 3090', output: 'GLB model', kind: 'voxel',
    input: { image: robot, style: 'mesh', foreground_ratio: 0.85, mc_resolution: 256 }, inputImage: robot,
    outputMedia: '/static/cogs/voxel-3d-output.webp', outputFile: 'https://appstatic.app.nz/static/cogs/voxel-3d-output.glb',
    source: 'https://github.com/lee101/appnz-voxel-3d', upstream: 'https://github.com/VAST-AI-Research/TripoSR',
    tags: ['TripoSR', 'GLB', 'voxel'], outputNote: 'Verified hosted result · colored TripoSR mesh GLB (1.9MB) from the exact input · voxel32/48/64 variants available',
  },
  {
    slug: 'lama-cleaner', template: 'lama-cleaner', title: 'LaMa Cleaner', eyebrow: 'Image + mask → clean plate',
    description: 'Remove unwanted objects with a white mask. LaMa fills the selected region, feathers the edge, and preserves pixels outside the repair.',
    hardware: 'RTX 3090', output: 'PNG image', kind: 'clean',
    input: { image: robot, mask: 'remove-object-mask.png', grow: 8, feather: 6 }, inputImage: robot,
    source: 'https://github.com/lee101/appnz-lama-cleaner', upstream: 'https://github.com/advimman/lama',
    tags: ['inpainting', 'object removal', 'ONNX'], outputNote: 'Output-format preview · verified artifact pending',
  },
  {
    slug: 'depth-vfx', template: 'depth-vfx', title: 'Depth VFX', eyebrow: 'Image → depth-powered passes',
    description: 'Run Depth Anything V2 once and export a depth map, heatmap, normal pass, or a subtle looping parallax move.',
    hardware: 'RTX 3090', output: 'MP4 or image pass', kind: 'depth',
    input: { image: robot, effect: 'parallax', strength: 0.55, seconds: 3 }, inputImage: robot,
    source: 'https://github.com/lee101/appnz-depth-vfx', upstream: 'https://huggingface.co/depth-anything/Depth-Anything-V2-Small-hf',
    tags: ['depth', 'parallax', 'VFX'], outputNote: 'Depth-pass visualization · verified artifact pending',
  },
  {
    slug: 'vectorizer', template: 'vectorizer', title: 'Vectorizer', eyebrow: 'Raster → editable SVG',
    description: 'Convert logos, posters, photos, and pixel art into compact SVG paths with VTracer’s deterministic multithreaded Rust core.',
    hardware: 'RTX 3090', output: 'SVG file', kind: 'vector',
    input: { image: 'https://app.nz/static/cogs/vectorizer-input.webp', preset: 'poster', speckle: 6, color_precision: 6 }, inputImage: '/static/cogs/vectorizer-input.webp',
    outputMedia: '/static/cogs/vectorizer-output.webp',
    source: 'https://github.com/lee101/appnz-vectorizer', upstream: 'https://github.com/visioncortex/vtracer',
    tags: ['SVG', 'VTracer', 'CPU-friendly'], outputNote: 'Verified hosted result · SVG paths returned',
  },
  {
    slug: 'image-upscaler', template: 'image-upscaler', title: 'Image Upscaler', eyebrow: 'Small image → restored 4× image',
    description: 'Restore detail with Real-ESRGAN at 2× or 4×, select general or anime tuning, and optionally enhance faces with GFPGAN.',
    hardware: 'RTX 3090', output: 'PNG or JPEG', kind: 'upscale',
    input: { image: 'https://app.nz/static/cogs/image-upscaler-input.webp', scale: 2, model: 'general', face_enhance: false }, inputImage: '/static/cogs/image-upscaler-input.webp',
    outputMedia: '/static/cogs/image-upscaler-output.webp',
    source: 'https://github.com/lee101/appnz-image-upscaler', upstream: 'https://github.com/xinntao/Real-ESRGAN',
    tags: ['Real-ESRGAN', 'GFPGAN', 'restoration'], outputNote: 'Verified hosted result · 2× raster output',
  },
  {
    slug: 'vocal-separator', template: 'vocal-separator', title: 'Vocal Separator', eyebrow: 'Song → clean stems',
    description: 'Split a track into vocals and instrumental, or export all four Demucs stems as a ZIP for remixing and post-production.',
    hardware: 'RTX 3090', output: 'Audio stems or ZIP', kind: 'stems',
    input: { audio: 'full-mix.wav', stems: 'two', format: 'wav', model: 'htdemucs' },
    source: 'https://github.com/lee101/appnz-vocal-separator', upstream: 'https://github.com/facebookresearch/demucs',
    tags: ['Demucs', 'vocals', 'music'], outputNote: 'Two synchronized audio outputs',
  },
  {
    slug: 'background-remover', template: 'background-remover', title: 'Background Remover', eyebrow: 'Image → transparent cutout',
    description: 'Remove a photo or illustration background to a transparent PNG with a warm, reusable segmentation session.',
    hardware: 'RTX 3090', output: 'Transparent PNG', kind: 'cutout',
    input: { image: 'https://app.nz/static/cogs/background-remover-input.webp', model: 'isnet-general-use' }, inputImage: '/static/cogs/background-remover-input.webp',
    outputMedia: '/static/cogs/background-remover-output.webp',
    source: 'https://github.com/lee101/appnz-rembg', upstream: 'https://github.com/danielgatis/rembg',
    tags: ['rembg', 'segmentation', 'transparent PNG'], outputNote: 'Verified hosted result · transparent PNG',
  },
  {
    slug: 'wan22-t2v-h100-test', aliases: ['wan22-t2v'], template: 'wan22-t2v', title: 'Wan 2.2 T2V + CG-Taylor', eyebrow: 'Prompt → accelerated video',
    description: 'Generate video with Wan 2.2 A14B using NF4 dual-stage transformers and confidence-gated Taylor caching for training-free acceleration.',
    hardware: 'H100 80GB', output: 'MP4 video', kind: 'video',
    input: { prompt: 'A beautiful anime fairy walking through an enchanted forest, soft sunlight filtering through the trees, cinematic tracking shot.', resolution: '480x480', num_frames: 81, steps: 27, guidance: 4, seed: 7, cgtaylor: true },
    outputMedia: 'https://appstatic.app.nz/static/cogs/wan22-t2v-output.mp4',
    source: 'https://github.com/lee101/accelerated-cgtaylor-wan-app-nz', upstream: 'https://huggingface.co/Wan-AI/Wan2.2-T2V-A14B-Diffusers',
    tags: ['Wan 2.2', 'CG-Taylor', 'NF4'], outputNote: 'Verified hosted result · 81-frame live MP4 from the exact input (L40S, cached weights)',
  },
  {
    slug: 'wan22-i2v', template: 'wan22-i2v', title: 'Wan 2.2 I2V + CG-Taylor', eyebrow: 'Image + prompt → accelerated video',
    description: 'Animate a reference frame while preserving its aspect ratio. The I2V image uses the same R2-backed weight path and 48 GB-friendly NF4 runtime.',
    hardware: 'H100 80GB', output: 'MP4 video', kind: 'video',
    input: { image: robot, prompt: 'The robot waves, then takes one step toward camera.', resolution: 'auto', num_frames: 81, steps: 27, cgtaylor: true }, inputImage: robot,
    outputMedia: '/static/uploads/playground/fal-video/veo-3.1-fast-image-to-video.mp4',
    source: 'https://github.com/lee101/accelerated-cgtaylor-wan-app-nz', upstream: 'https://huggingface.co/Wan-AI/Wan2.2-I2V-A14B-Diffusers',
    tags: ['Wan 2.2', 'image-to-video', 'R2 weights'], outputNote: 'Video-format preview · verified Cog result pending',
  },
  {
    slug: 'wan-animate-2', aliases: ['video-restyle-shared', 'wan-animate-shared'], template: 'wan-animate-2',
    title: 'Wan Animate 2', eyebrow: 'Character image + driving video → restyled motion',
    description: 'Transfer the motion, expression, and timing of a driving clip onto a reference character while preserving identity, clothing, and the reference composition.',
    hardware: 'L40S 48GB', output: 'MP4 video', kind: 'video',
    input: {
      image: 'https://appstatic.app.nz/static/cogs/wan-animate-2/reference.png',
      driving_video: 'https://appstatic.app.nz/static/cogs/wan-animate-2/driving.mp4',
      prompt: 'A full-body character dancing naturally, stable identity and clothing, continuous motion.',
      quality: 'preview', max_seconds: 1, fps: 12, frames_per_segment: 17, steps: 6,
      seed: 43, preserve_audio: false, cgtaylor: false, cgtaylor_threshold: 0.015,
    },
    inputImage: 'https://appstatic.app.nz/static/cogs/wan-animate-2/reference.png',
    inputVideo: 'https://appstatic.app.nz/static/cogs/wan-animate-2/driving.mp4',
    outputMedia: 'https://appstatic.app.nz/cogs/1c6cf879-d533-49bf-ac31-2280713a0385/output.mp4',
    source: 'https://github.com/lee101/wan-animate-cog', upstream: 'https://github.com/Wan-Video/Wan-Animate-2',
    tags: ['Wan Animate 2', 'character animation', 'driving video', 'FP8'],
    outputNote: 'Verified hosted result · exact reference, driving clip, settings, and output',
  },
  {
    slug: 'pocket-tts', template: 'pocket-tts', title: 'Pocket TTS', eyebrow: 'Text + voice → speech',
    description: 'Fast 100M-parameter speech synthesis with 26 voices across six languages and optional authorized voice cloning from a sample.',
    hardware: 'RTX 3090', output: 'MP3, Opus, or WAV', kind: 'speech',
    input: { text: 'Kia ora. Your scale-to-zero voice model is ready.', voice: 'alba', format: 'mp3' },
    source: 'https://github.com/lee101/pocket-tts-cog', upstream: 'https://github.com/kyutai-labs/pocket-tts',
    tags: ['TTS', 'multilingual', 'voice cloning'], outputNote: 'Streaming audio output',
  },
  {
    slug: 'appnz-tts', template: 'appnz-tts', title: 'app.nz TTS', eyebrow: 'Text + reference → expressive speech',
    description: 'Fish Speech synthesis with stored or inline reference voices, reusable model cache, and serverless-to-warm-pod promotion.',
    hardware: 'RTX 3090', output: 'MP3, Opus, or WAV', kind: 'speech',
    input: { input: "Hi, let's think through the best things in the world.", voice: 'studio-narrator', response_format: 'mp3', speed: 1 },
    source: 'https://github.com/lee101/app-site/tree/master/replicatecog/appnz-tts', upstream: 'https://github.com/fishaudio/fish-speech',
    tags: ['Fish Speech', 'reference voice', 'serverless'], outputNote: 'Expressive audio output',
  },
  {
    slug: 'minimax-h3', template: 'minimax-h3', title: 'MiniMax H3', eyebrow: 'Text → video + native audio',
    description: 'MiniMax H3 text-to-video with structured audiovisual prompts, GPU AV1 WebM delivery, R2/HF weight cache, and optional EasyCache operator sweeps in the open-source adapter.',
    hardware: 'RTX 5090', output: 'WebM AV1 + stereo audio', kind: 'video',
    input: {
      prompt: 'Macro product film in one continuous shot: a translucent glass hummingbird unfolds its wings above a dark reflective table, the camera makes a slow 30-degree arc, cool rim light refracts into spectral highlights, delicate crystalline wing chimes and a low room tone, no cuts, photorealistic.',
      aspect_ratio: '16:9', size: 'balanced', duration: 5, steps: 20, seed: 31001,
      structured_prompt: true, include_audio: true, output_codec: 'webm-av1', encode_quality: 26,
    },
    outputMedia: '/static/cogs/h3/glass-hummingbird-t2v.webm',
    source: 'https://github.com/lee101/h3-cog', upstream: 'https://huggingface.co/MiniMaxAI/MiniMax-H3',
    tags: ['MiniMax H3', 'native audio', 'AV1'], outputNote: 'glass-hummingbird T2V · 1184×672 · 5s · seed 31001',
  },
  {
    slug: 'minimax-h3-i2v', template: 'minimax-h3', title: 'MiniMax H3 I2V', eyebrow: 'Image + prompt → video',
    description: 'Animate a supplied first frame while preserving identity and layout. Uses the same minimax-h3 template with first_frame conditioning and native stereo audio.',
    hardware: 'RTX 5090', output: 'WebM AV1 + stereo audio', kind: 'video',
    input: {
      prompt: 'One continuous shoulder-height tracking shot alongside the tram as it begins to move through gentle night rain; reflections stretch across wet pavement, passengers shift naturally behind the windows, soft wheel rumble, rain ticks and a distant crossing bell, preserve the tram design and faces.',
      aspect_ratio: '16:9', size: 'balanced', duration: 5, steps: 20, seed: 31002,
      structured_prompt: true, include_audio: true, output_codec: 'webm-av1',
      first_frame: '/static/cogs/h3/tram-rain-i2v-first.png',
    },
    inputImage: '/static/cogs/h3/tram-rain-i2v-first.png',
    source: 'https://github.com/lee101/h3-cog', upstream: 'https://huggingface.co/MiniMaxAI/MiniMax-H3',
    tags: ['MiniMax H3', 'image-to-video', 'keyframes'], outputNote: 'tram-rain I2V · first frame in static/cogs/h3',
  },
  {
    slug: 'minimax-h3-talk', template: 'minimax-h3', title: 'MiniMax H3 Talk', eyebrow: 'Portrait + uploaded audio → talking video',
    description: 'Drive mouth timing and delivery from an uploaded speech clip via H3 Ref2VA. Upload a face (first_frame) plus 2–15s of talking audio; duration snaps to the clip.',
    hardware: 'RTX 5090', output: 'WebM AV1 + stereo audio', kind: 'video',
    input: {
      prompt: 'Medium close-up of the person in the reference portrait speaking warmly to camera; natural head motion, soft studio key light, shallow depth of field, mouth and expression follow the uploaded speech exactly.',
      aspect_ratio: '9:16', size: 'balanced', duration: 5, steps: 20, seed: 31008,
      structured_prompt: true, include_audio: true, output_codec: 'webm-av1',
      first_frame: '/static/cogs/h3/talking-portrait.png',
      audio: '/static/cogs/h3/talking-hello.wav',
    },
    inputImage: '/static/cogs/h3/talking-portrait.png',
    inputAudio: '/static/cogs/h3/talking-hello.wav',
    source: 'https://github.com/lee101/h3-cog', upstream: 'https://huggingface.co/MiniMaxAI/MiniMax-H3',
    tags: ['MiniMax H3', 'audio-driven', 'Ref2VA', 'talking'], outputNote: 'Upload audio + portrait · Ref2VA mouth sync',
  },
  {
    slug: 'yue2', template: 'yue2', title: 'YuE2 Song', eyebrow: 'Lyrics + style → full song',
    description: 'Turn tagged lyrics and a style prompt into a 48kHz stereo song: YuE2 plans melody and chords, sings with bf16 AR + flow-matching NAR under CUDA graphs, and decodes through a tiled VAE. Scale-to-zero RunPod serverless.',
    hardware: 'RTX 4090 24GB', output: 'MP3, WAV, or FLAC', kind: 'music',
    input: {
      style: 'English, upbeat synth-pop, bright male voice, punchy synth bass, neon pads, driving beat, 116 BPM',
      lyrics: '[Verse]\nCity lights are calling out my name\nMidnight streets all look the same\n[Chorus]\nWe are electric, we are gold\nStories that will never get old',
      cot: 'full', seed: 831002, ode_steps: 32, semantic_max_tokens: 3000, format: 'mp3',
    },
    outputMedia: '/static/cogs/yue2/synthpop.mp3',
    source: 'https://github.com/lee101/yue-cog', upstream: 'https://github.com/multimodal-art-projection/YuE',
    tags: ['YuE2', 'lyrics-to-song', 'music', 'serverless'],
    outputNote: 'Verified local result · 60.9s stereo MP3 from the exact input (seed 831002) · pop-piano + folk variants in /static/cogs/yue2',
  },
];

export const cogShowcaseBySlug = Object.fromEntries(
  cogShowcases.flatMap((showcase) => [showcase.slug, ...(showcase.aliases || [])].map((slug) => [slug, showcase])),
) as Record<string, CogShowcase>;

export const cogShowcaseByTemplate = Object.fromEntries(
  [...cogShowcases].reverse().map((showcase) => [showcase.template, showcase]),
) as Record<string, CogShowcase>;
// reverse so the first listed showcase for a shared template wins (minimax-h3 over -i2v/-talk)

export const cogShowcasePaths = cogShowcases.map((showcase) => `/cogs/${showcase.slug}`);
